import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, MessageSquare, User, Bot, ChevronDown, ChevronRight } from 'lucide-react';
import { aiConfigApi } from './services/aiChatbotApi';
import { useToast } from '../email/shared/ToastProvider';
import './AIChatbot.css';

const AIChatbotLogs = () => {
  const toast = useToast();
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [phoneFilter, setPhoneFilter] = useState('');
  const [usage, setUsage]       = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (phoneFilter.trim()) params.phone = phoneFilter.trim();
      const [logsRes, usageRes] = await Promise.all([
        aiConfigApi.listLogs(params),
        aiConfigApi.getUsage(),
      ]);
      if (logsRes.data?.entity) setLogs(logsRes.data.entity);
      if (usageRes.data?.entity) setUsage(usageRes.data.entity);
    } catch (err) {
      console.error('Logs load error:', err);
      toast.error('Failed to load conversation logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [phoneFilter, toast]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (loading) {
    return <div className="ai-loader"><Loader2 size={24} className="spin" /> &nbsp; Loading conversations…</div>;
  }

  /* ── usage stats ─────────────────────────────────── */
  const used = usage?.creditsUsed ?? 0;
  const total = usage?.creditsTotal ?? 1000;
  const pct = Math.min(100, Math.round((used / Math.max(total, 1)) * 100));
  const usageClass = pct > 90 ? 'danger' : pct > 70 ? 'warn' : '';

  return (
    <>
      <div className="ai-stats">
        <div className="ai-stat">
          <div className="ai-stat-label">Total Conversations</div>
          <div className="ai-stat-value">{logs.length}</div>
          <div className="ai-stat-sub">last 50 messages</div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Credits This Month</div>
          <div className="ai-stat-value">{used} <span style={{ fontSize: 14, color: '#94a3b8' }}>/ {total}</span></div>
          <div className="ai-usage-bar">
            <div className={`ai-usage-fill ${usageClass}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Avg Response Time</div>
          <div className="ai-stat-value">
            {logs.length
              ? Math.round(logs.reduce((s, l) => s + (l.response_time_ms || 0), 0) / logs.length)
              : 0} <span style={{ fontSize: 14, color: '#94a3b8' }}>ms</span>
          </div>
          <div className="ai-stat-sub">avg LLM round-trip</div>
        </div>
      </div>

      {/* Filters */}
      <div className="ai-card">
        <div className="ai-card-header">
          <div>
            <h3 className="ai-card-title">Conversations</h3>
            <p className="ai-card-subtitle">All AI replies sent on your WhatsApp number. Click a row to see the full message.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="ai-input"
              style={{ width: 200 }}
              placeholder="Filter by phone…"
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value)}
            />
            <button
              className="ai-btn ai-btn-secondary"
              onClick={() => { setRefreshing(true); fetchLogs(); }}
              disabled={refreshing}
            >
              <RefreshCw size={13} className={refreshing ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="ai-empty">
            <div className="ai-empty-icon"><MessageSquare size={40} /></div>
            <div>No conversations yet. The bot will start logging here once it replies to customers.</div>
          </div>
        ) : (
          <table className="ai-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>When</th>
                <th>Phone</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Chunks</th>
                <th>Tokens</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <React.Fragment key={l.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => toggle(l.id)}>
                    <td>{expanded[l.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td style={{ color: '#64748b', fontSize: 13 }}>
                      {l.created_at ? new Date(l.created_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{l.phone || '—'}</td>
                    <td>{l.provider || '—'}</td>
                    <td style={{ fontSize: 13, color: '#64748b' }}>{l.model || '—'}</td>
                    <td>{l.retrieved_chunks ?? 0}</td>
                    <td>{l.total_tokens ?? '—'}</td>
                    <td style={{ fontSize: 13, color: '#64748b' }}>{l.response_time_ms ? `${l.response_time_ms}ms` : '—'}</td>
                  </tr>
                  {expanded[l.id] && (
                    <tr>
                      <td colSpan={8} style={{ background: '#f8fafc' }}>
                        <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {l.user_message && (
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <User size={14} color="#4f46e5" />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Customer</div>
                                <div style={{ fontSize: 14 }}>{l.user_message}</div>
                              </div>
                            </div>
                          )}
                          {l.bot_reply && (
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Bot size={14} color="#16a34a" />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>AI Reply</div>
                                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{l.bot_reply}</div>
                              </div>
                            </div>
                          )}
                          {l.error_message && (
                            <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>
                              <b>Error:</b> {l.error_message}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default AIChatbotLogs;
